import { CloseCircleOutlined } from "@ant-design/icons";
import { LoginRequestDTO } from "@dto/session.dto";
import { WelcomeMessagePosition } from "@dto/settings.dto";
import { Button, Input, InputProps, InputRef } from 'antd';
import { ChangeEvent, CSSProperties, forwardRef, SyntheticEvent, useEffect, useReducer, useRef, useState } from "react";
import { useAppGlobStore } from "src/appGlobalStore";
import { XmSpinner } from "src/component/spinner/XmSpinner";
import { API_URL } from "src/data/Api";
import { RoutePath } from "src/data/Routes";
import { useTheme } from "src/theme/ThemeProvider";
import { GLOB } from "../../util/Glob";
import { ChangeExpiredPasswordModal, ChangePasswordProps } from "./ChangeExpiredPasswordModal";
import "./Login.less";

type logInput = "username" | "password";

/** Give up on a pending SSO popup after this long, so an abandoned login does not leak its listener */
const SSO_TIMEOUT_MS = 10 * 60_000;

export interface InputItem extends InputProps {
  inputKey: logInput;
  value: string;
  errMsg: string;
  error: boolean;
};

interface InitState {
  username: InputItem;
  password: InputItem;
  error: string;
}

export enum act {
  SET = "set-input",
  EMPTY = "empty-input",
  SUB_ERR = "submit-error",
}

const ERROR_MSG = {
  EMPTY: '',
  NO_USER: "The Username field is required.",
  NO_PASS: "The Password field is required.",
  BOTH: "The Username and Password fields are required.",
} as const;

const initialState: InitState = {
  username: {
    inputKey: "username",
    type: "text",
    placeholder: "Username",
    autoComplete: "username",
    value: "",
    errMsg: ERROR_MSG.NO_USER,
    error: false
  },
  password: {
    inputKey: "password",
    type: "password",
    placeholder: "Password",
    autoComplete: "current-password",
    value: "",
    errMsg: ERROR_MSG.NO_PASS,
    error: false
  },
  error: ERROR_MSG.EMPTY
};

const reducer = (state: InitState, { type, input, error }: { type: act, input?: { inputKey: logInput, value?: string; }, error?: string; }): InitState => {
  const inputKey = input?.inputKey; /// just simplification of variable

  const setError = () => {
    if (!state.error || state.error === state[inputKey].errMsg)
      return "";

    if (state.error === ERROR_MSG.BOTH) {
      if (inputKey === state.username.inputKey)
        return ERROR_MSG.NO_PASS;

      return ERROR_MSG.NO_USER;
    }

    return state.error;
  };

  if (type === act.SET)
    return {
      ...state,
      [inputKey]: { ...state[inputKey], value: input.value, error: false },
      error: setError()
    };

  if (type === act.EMPTY) {
    if (state[inputKey].value || state.error == ERROR_MSG.BOTH)
      return state;

    return {
      ...state,
      error: state.error && (state.error == ERROR_MSG.NO_PASS || state.error == ERROR_MSG.NO_USER) && state.error !== state[inputKey].errMsg ? ERROR_MSG.BOTH : state[inputKey].errMsg,
      [inputKey]: { ...state[inputKey], error: true }
    };
  }

  if (type === act.SUB_ERR)
    return { ...initialState, error };
};

export function Login() {
  const [{ username, password, error }, dispatch] = useReducer(reducer, initialState);
  const userInputRef = useRef<InputRef>(null);
  const passInputRef = useRef<InputRef>(null);
  const { demo, firstLogin, isSSO } = useAppGlobStore(state => state.info.backend) ?? {};
  const welcome_message = useAppGlobStore(state => state.info.backend?.welcome_message);
  const [changePasswordProps, setChangePasswordProps] = useState<ChangePasswordProps>();

  const { logoSrc } = useTheme();
  const [isLogging, setIsLogging] = useState(false);
  const ssoCleanup = useRef<(() => void) | null>(null);

  useEffect(() => {
    if (globalThis.location.pathname.endsWith(RoutePath.LOGOUT))
      globalThis.history.replaceState(null, "XorMon", GLOB.getPublicUrl() ? GLOB.getPublicUrl() : '/');

    userInputRef.current?.focus();

    return () => {
      ssoCleanup.current?.();
    };
  }, []);

  useEffect(() => {
    if (demo)
      initialState.password.value = initialState.username.value = 'xormon';
  }, [demo]);

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();

    if (!password.value || !username.value) {
      if (!password.value) {
        dispatch({ type: act.EMPTY, input: { inputKey: password.inputKey } });
        passInputRef.current.focus();
      }

      if (!username.value) {
        dispatch({ type: act.EMPTY, input: { inputKey: username.inputKey } });
        userInputRef.current.focus();
      }

      return;
    }

    const timer = globalThis.setTimeout(() => {
      setIsLogging(true);
    }, 500);

    const login: LoginRequestDTO = { username: username.value, password: password.value };
    const response = await GLOB.authService.logIn(login);
    setIsLogging(false);
    clearTimeout(timer);
    userInputRef.current?.focus();
    if (!GLOB.authService.isLoggedIn()) {
      dispatch({ type: act.SUB_ERR, error: response as string });
      if (response === 'Expired password')
        setChangePasswordProps({ initialUsername: username.value, timestamp: Date.now() });
    }
  };

  const handleChange = (inputKey: logInput) => ({ target: { value } }: ChangeEvent<HTMLInputElement>) => dispatch({ type: act.SET, input: { inputKey, value } });

  const onSSO = () => {
    ssoCleanup.current?.(); // an earlier attempt may still be listening
    // MS Entra ID sends Cross-Origin-Opener-Policy headers that sever window.opener, so the popup
    // reports back over a same-origin BroadcastChannel; window.opener is the fallback for DEV (cross-origin).
    const channel = 'BroadcastChannel' in globalThis ? new BroadcastChannel('xormon-sso') : null;
    let done = false;
    const expiry = globalThis.setTimeout(() => {
      cleanup();
      dispatch({ type: act.SUB_ERR, error: 'SSO login timed out! Please try again.' });
    }, SSO_TIMEOUT_MS);

    // a COOP-severed popup reports closed===true right away, so polling popup.closed would abort the very
    // flow the BroadcastChannel exists for - unmount and the timeout are the only safe teardown triggers
    const cleanup = () => {
      if (done) return;
      done = true;
      clearTimeout(expiry);
      globalThis.removeEventListener('message', listener);
      channel?.close();
      ssoCleanup.current = null;
    };

    const finish = async (data?: { search?: string; hash?: string; }) => {
      const { search, hash } = data ?? {};
      if (done || search == null || hash == null) return;
      cleanup();
      try { popup?.close(); } catch { /* opener severed by COOP */ }
      const response = await GLOB.authService.logIn(null, GLOB.SERVER_URL + API_URL.OIDC + '/login' + search + hash);
      if (!GLOB.authService.isLoggedIn())
        dispatch({ type: act.SUB_ERR, error: response as string });
    };

    // accept messages only from the SSO callback page origin (backend origin in DEV, own origin in PROD),
    // otherwise any window could inject its own search/hash into the OIDC login request
    const trustedOrigin = new URL(GLOB.SERVER_URL || '/', window.location.href).origin;
    const listener = (event: MessageEvent) => {
      if (event.origin !== trustedOrigin) return;
      return finish(event?.data);
    };
    globalThis.addEventListener('message', listener);
    if (channel) channel.onmessage = (event: MessageEvent) => finish(event.data);
    ssoCleanup.current = cleanup;

    const popup = globalThis.open('', '', 'height=500,width=500,scrollbars=1');
    if (!popup) {
      cleanup();
      dispatch({ type: act.SUB_ERR, error: 'Popup was blocked! Allow popups for this site to use SSO login.' });
      return;
    }
    popup.document.title = 'XorMon SSO';
    popup.document.body.textContent = 'Authorizing...';
    //Start the SSO login sequence using the request URL
    popup.location = GLOB.SERVER_URL + API_URL.OIDC + '/redirect';
  };

  const wrapperStyle: CSSProperties = {};
  if (welcome_message?.GLOB_WELCOME_MESSAGE && welcome_message?.GLOB_WELCOME_MESSAGE_ENABLED) {
    switch (welcome_message.GLOB_WELCOME_MESSAGE_POSITION) {
      case WelcomeMessagePosition.TOP: {
        wrapperStyle.flexDirection = 'column';
        wrapperStyle.paddingBottom = 0;
        break;
      }
      case WelcomeMessagePosition.BOTTOM: {
        wrapperStyle.flexDirection = 'column-reverse';
        wrapperStyle.paddingBottom = 0;
        break;
      }
      case WelcomeMessagePosition.LEFT: {
        wrapperStyle.flexDirection = 'row';
        break;
      }
      case WelcomeMessagePosition.RIGHT: {
        wrapperStyle.flexDirection = 'row-reverse';
        break;
      }
    }
  }

  return (
    <div className={`login-wrapper ${demo ? 'demo' : ''}`} style={wrapperStyle}>
      <ChangeExpiredPasswordModal {...changePasswordProps} />
      {welcome_message?.GLOB_WELCOME_MESSAGE_ENABLED && <div className="xm-welcome-msg" dangerouslySetInnerHTML={{ __html: welcome_message.GLOB_WELCOME_MESSAGE }} />}
      <div className="xm-login-main-container xm-flex">
        <div className="login-container xm-flex">
          <div className="logo">
            <img src={logoSrc} alt="xormon_logo" />
          </div>
          <div className="login-form-container">
            <form className="login-form" onSubmit={handleSubmit}>
              {[username, password].map(({ inputKey, errMsg, ...input }) =>
                <LoginInput
                  key={inputKey}
                  inputKey={inputKey}
                  {...input}
                  disabled={isLogging}
                  onChange={handleChange(inputKey)}
                  //onBlur={handleBlur(inputKey)}
                  ref={inputKey === 'username' ? userInputRef : passInputRef}
                />
              )}
              <div className="form-item-wrapper">

                <Button htmlType='submit' disabled={isLogging} className={isLogging ? "xm-login-form-button form-item inactive" : "xm-login-form-button form-item active"}>
                  {isLogging ? <><XmSpinner />{"Sign in"}</> : "Sign in"}
                </Button>
              </div>
            </form>
          </div>
        </div>
        {isSSO && <Button className="xm-sso-btn" onClick={onSSO}>Continue with Single Sign-On</Button>}
        {error &&
          <div className="login-error-wrapper xm-flex">
            <div className="login-error-container">
              <CloseCircleOutlined className="login-error-icon" />
              <span className="login-error-text">{error}</span>
            </div>
          </div>
        }

        {!demo && firstLogin && <div className="xm-demo" style={{ width: '25em' }}>
          Default system account credentials:
          <div>Username/password: xormon/xormon</div>
        </div>}
        {demo && <>
          <div className="xm-demo">
            <div>Username/Password: xormon/xormon</div>
          </div>
          <div className="xm-demo">
            Would you be interested in a live session with one of our experts<br />
            to show you all the features of the product?<br />
            <a href="https://xormon.com/live_form.html" target="_blank">Apply here</a>
          </div>
        </>}
      </div>
    </div>
  );
}

const LoginInput = forwardRef<InputRef, Omit<InputItem, 'errMsg'>>(function LoginInput({ error, inputKey, ...rest }, ref) {
  const inputProps = {
    ...rest,
    className: error ? "form-input form-item form-input-error" : "form-input form-item",
    ref
  };

  return (
    <label className="form-item-wrapper">
      {inputKey === 'username' ?
        <Input {...inputProps} /> :
        <Input.Password
          {...inputProps}
        />
      }
    </label>
  );
});